import { SOURCE_CAROUSEL_ID } from '../../utils/constants';

const HOME_BANNER_TYPE = 'home-banner';
const TOAST_BANNER_TYPE = 'toast-banner';

const COMMUNICATION_BANNER_TYPES_NORMALIZED = new Set([
  HOME_BANNER_TYPE,
  TOAST_BANNER_TYPE,
  'dialog-banner',
  'dialog-ads',
  'communication-dialog',
  'dialog',
  String(SOURCE_CAROUSEL_ID.ROW_ADS || '').trim().toLowerCase().replace(/_/g, '-'),
]);
const DEFAULT_SOLID_BACKGROUND = '#282828';

function trimString(value) {
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t || undefined;
}

/**
 * @param {unknown} raw
 * @returns {object|undefined}
 */
function unwrapCommunicationPayload(raw) {
  if (!raw || typeof raw !== 'object') return undefined;
  if (typeof raw.type === 'string' && raw.type.trim()) return raw;
  if (raw.carousel && typeof raw.carousel === 'object') return raw.carousel;
  if (raw.data && typeof raw.data === 'object') return raw.data;
  return raw;
}

function normalizeCommunicationType(payload) {
  if (!payload || typeof payload !== 'object') return '';
  return typeof payload.type === 'string'
    ? payload.type.trim().toLowerCase()
    : '';
}

function resolveBannerMedia(payload) {
  const title = trimString(payload.title);
  const imageUrlTrim = trimString(payload.imageUrl ?? payload.imageURL);
  const logoUrl = trimString(payload.logoUrl ?? payload.logoURL) || imageUrlTrim;
  const backgroundUrlTrim = trimString(
    payload.backgroundUrl ?? payload.backgroundURL ?? payload.background_image,
  ) || imageUrlTrim;
  const backgroundColorRaw = trimString(payload.backgroundColor);
  const hasBgUrl = Boolean(backgroundUrlTrim);
  const hasExplicitBgColor = Boolean(backgroundColorRaw);
  let backgroundColor = DEFAULT_SOLID_BACKGROUND;
  if (hasBgUrl) {
    backgroundColor = undefined;
  } else if (hasExplicitBgColor) {
    backgroundColor = backgroundColorRaw;
  }

  return {
    title,
    logoUrl,
    backgroundUrl: hasBgUrl ? backgroundUrlTrim : undefined,
    backgroundColor,
    hasExplicitBgColor,
  };
}

function resolveBannerCta(payload) {
  const { cta, link } = payload;
  let ctaSource;
  if (cta && typeof cta === 'object') {
    ctaSource = cta;
  } else if (link && typeof link === 'object') {
    ctaSource = link;
  } else if (typeof link === 'string') {
    ctaSource = { url: link };
  }

  return {
    ctaText: trimString(ctaSource?.text),
    ctaLink: trimString(ctaSource?.link ?? ctaSource?.url ?? ctaSource?.href),
  };
}

function hasRenderableContent({
  title,
  logoUrl,
  backgroundUrl,
  ctaLink,
  hasExplicitBgColor,
}) {
  return Boolean(
    title
    || logoUrl
    || backgroundUrl
    || ctaLink
    || hasExplicitBgColor,
  );
}

/**
 * @param {unknown} raw
 * @returns {{
 *  type: string,
 *  title: string|undefined,
 *  logoUrl: string|undefined,
 *  backgroundUrl: string|undefined,
 *  backgroundColor: string|undefined,
 *  ctaText: string|undefined,
 *  ctaLink: string|undefined
 * }|undefined}
 */
export function mapHomeBannerPayload(raw) {
  const payload = unwrapCommunicationPayload(raw);
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const typeNorm = normalizeCommunicationType(payload);
  if (!typeNorm || !COMMUNICATION_BANNER_TYPES_NORMALIZED.has(typeNorm)) {
    return undefined;
  }

  const media = resolveBannerMedia(payload);
  const cta = resolveBannerCta(payload);
  if (!hasRenderableContent({ ...media, ...cta })) {
    return undefined;
  }

  return {
    type: typeNorm,
    title: media.title,
    logoUrl: media.logoUrl,
    backgroundUrl: media.backgroundUrl,
    backgroundColor: media.backgroundColor,
    ctaText: cta.ctaText,
    ctaLink: cta.ctaLink,
  };
}
